// useLiveConversation — L3 Talk (Douglas plan T20, D10).
//
// A live voice conversation in front of ONE agent chat. The realtime voice
// model (Azure Realtime, minted by POST /assistant/realtime/token) is only a
// mouth and ears: its one tool is ask_agent(text), which this hook turns into
// the chat's own sendMessage(text) — the same message Send would post, so the
// chat's messages are exactly what was asked. The call is answered at once
// ("Sent to the agent."); when the agent's turn completes, the reply (and any
// ask_user question) is added to the voice conversation as an item and the
// voice is asked to speak it. It has no page tools, no server tools and no
// memory.
//
// The host passes its session (the main chat's, or a page helper's, whose
// sendMessage already carries the page's tools). On the start screen there is
// no chat yet: its sendMessage creates one, as Send does, and the page moves to
// the new chat. The connection is then handed over, not closed: the chat
// page's hook picks it up and speaks the first reply. Nobody picking it up
// within HANDOVER_MS closes it.

import { useEffect, useRef, useState } from 'react'
import axios from 'axios'
import { API_BASE_URL } from '../constants/system'
import type { AgentMessage, AskUserQuestionInfo } from '../agent/types'

export const ASK_AGENT = 'ask_agent'
/** What ask_agent returns to the voice model, at once. */
export const SENT_TO_AGENT = 'Sent to the agent.'
/** How long a connection handed over by the start screen waits for the chat page. */
export const HANDOVER_MS = 15_000
/** The most of a reply the voice is given; the rest is on screen. */
const REPLY_MAX_CHARS = 4_000

export type LiveState = 'off' | 'connecting' | 'listening' | 'speaking' | 'waiting'

export interface LiveCaption {
  /** `you`: what the voice heard; `voice`: what it is saying. */
  speaker: 'you' | 'voice'
  text: string
}

/** The pieces of a chat the live conversation reads (useAgentSession's own fields). */
export interface LiveConversationSession {
  /** The chat. Null on the start screen, before Send has created one. */
  session?: { id: string } | null
  messages: AgentMessage[]
  isStreaming: boolean
  error?: string | null
  askedQuestions?: Map<string, AskUserQuestionInfo>
  /** Messages sent during a turn, waiting for it to end. */
  queuedMessages?: AgentMessage[]
  /** Send the person's words into the chat, as Send does. A throw is told to the voice. */
  sendMessage: (content: string) => Promise<void> | void
}

/** One open realtime connection: events go out on its data channel. */
export interface LiveLink {
  send: (event: Record<string, unknown>) => void
  close: () => void
}

export interface LiveConnectHandlers {
  /** The data channel is open (called after connect has returned). */
  onOpen: () => void
  onEvent: (event: Record<string, unknown>) => void
  /** The connection dropped; the reason is shown. */
  onLost: (reason: string) => void
}

export type LiveConnect = (req: { customer: string; voice?: string }, handlers: LiveConnectHandlers) => Promise<LiveLink>

export interface UseLiveConversationOptions {
  customer: string
  /** A realtime voice id; absent: the server's default. */
  voice?: string
  /** How to open the connection. Default: WebRTC to Azure Realtime (realtimeConnect). Tests pass a fake. */
  connect?: LiveConnect
}

/** A reply the voice is waiting for: the turn started by the latest ask_agent. */
interface Awaiting {
  /** The first message index the turn can be at. */
  sinceIndex: number
  /** The chat it was sent to; null when the start screen sent it (the chat is being created). */
  sessionId: string | null
  sawStreaming: boolean
  errorBefore: string | null
}

/** A live connection; the start screen hands one over to the chat page. */
interface Live {
  link: LiveLink
  /** The link calls through this, so whichever hook holds the connection hears it. */
  handlers: { current: LiveConnectHandlers }
  awaiting: Awaiting | null
  /** Function-call ids answered (Azure sends each call twice). */
  handledCalls: Set<string>
  open: boolean
}

const NO_HANDLERS: LiveConnectHandlers = { onOpen: () => {}, onEvent: () => {}, onLost: () => {} }

// One microphone, so one live conversation per tab: the one handed over, if any.
let handedOver: { live: Live; timer: ReturnType<typeof setTimeout> } | null = null

export function __resetLiveConversationForTests() {
  if (handedOver) clearTimeout(handedOver.timer)
  handedOver = null
}

const clip = (s: string) => (s.length > REPLY_MAX_CHARS ? `${s.slice(0, REPLY_MAX_CHARS)} … (the rest is on screen)` : s)

/** The item the voice is given when a turn ends: the reply, and any question the agent asked. */
export function agentReplyText(reply: AgentMessage | undefined, questions: AskUserQuestionInfo[], error?: string | null): string {
  const parts: string[] = []
  if (reply?.failed) parts.push(`The agent's turn failed: ${reply.failed}.`)
  else if (reply?.stopped) parts.push('The agent was stopped before it finished.')
  const content = reply?.content?.trim()
  if (content) parts.push(`The agent replied:\n${clip(content)}`)
  for (const q of questions) {
    const options = q.options.map((o) => o.label).filter(Boolean)
    parts.push(`The agent asks: ${q.question}${options.length ? ` Options: ${options.join('; ')}.` : ''}`)
  }
  if (parts.length === 0) parts.push(error ? `The agent could not answer: ${error}` : 'The agent finished without a reply.')
  return `[Agent reply] ${parts.join('\n\n')}`
}

export default function useLiveConversation(session: LiveConversationSession, options: UseLiveConversationOptions) {
  const [phase, setPhase] = useState<'off' | 'connecting' | 'on'>('off')
  const [speaking, setSpeaking] = useState(false)
  const [waiting, setWaiting] = useState(false)
  const [caption, setCaption] = useState<LiveCaption | null>(null)
  const [error, setError] = useState<string | null>(null)

  const sessionRef = useRef(session)
  sessionRef.current = session
  const optionsRef = useRef(options)
  optionsRef.current = options
  const liveRef = useRef<Live | null>(null)
  // Bumped by stop and unmount, so a connect still in flight is closed when it lands.
  const startSeqRef = useRef(0)
  const voiceTextRef = useRef('')
  // Azure sends output_audio_buffer events over WebRTC; without them speaking ends at response.done.
  const sawBufferEventsRef = useRef(false)

  const send = (event: Record<string, unknown>) => liveRef.current?.link.send(event)

  /** Add a message for the voice to hear, and ask it to answer. */
  const say = (text: string) => {
    send({ type: 'conversation.item.create', item: { type: 'message', role: 'user', content: [{ type: 'input_text', text }] } })
    send({ type: 'response.create' })
  }

  const setAwaiting = (a: Awaiting | null) => {
    if (liveRef.current) liveRef.current.awaiting = a
    setWaiting(a !== null)
  }

  const answerCall = (callId: string, output: string) => {
    send({ type: 'conversation.item.create', item: { type: 'function_call_output', call_id: callId, output } })
    send({ type: 'response.create' })
  }

  const dispatchCall = (name: string, callId: string, rawArgs: string) => {
    const live = liveRef.current
    if (!live || !callId || live.handledCalls.has(callId)) return
    live.handledCalls.add(callId)
    let args: Record<string, unknown> = {}
    try {
      args = JSON.parse(rawArgs || '{}') as Record<string, unknown>
    } catch {
      /* bad JSON from the model: no text */
    }
    if (name !== ASK_AGENT) {
      answerCall(callId, JSON.stringify({ error: `There is no tool called ${name}. Your only tool is ${ASK_AGENT}.` }))
      return
    }
    const text = typeof args.text === 'string' ? args.text.trim() : ''
    if (!text) {
      answerCall(callId, JSON.stringify({ error: `${ASK_AGENT} needs the person's request as text.` }))
      return
    }
    answerCall(callId, SENT_TO_AGENT)
    const s = sessionRef.current
    setAwaiting({ sinceIndex: s.messages.length, sessionId: s.session?.id ?? null, sawStreaming: false, errorBefore: s.error ?? null })
    const notSent = (err: unknown) => {
      setAwaiting(null)
      const why = err instanceof Error ? err.message : 'unknown error'
      say(`[Not sent] The message could not be sent to the agent: ${why} Tell the person.`)
    }
    try {
      Promise.resolve(s.sendMessage(text)).catch(notSent)
    } catch (err) {
      notSent(err)
    }
  }

  const onEvent = (m: Record<string, unknown>) => {
    switch (m.type) {
      case 'conversation.item.input_audio_transcription.completed':
        if (typeof m.transcript === 'string' && m.transcript.trim()) setCaption({ speaker: 'you', text: m.transcript.trim() })
        break
      case 'response.created':
        voiceTextRef.current = ''
        break
      case 'response.audio_transcript.delta':
      case 'response.output_audio_transcript.delta':
        if (typeof m.delta === 'string') {
          voiceTextRef.current += m.delta
          setCaption({ speaker: 'voice', text: voiceTextRef.current })
          setSpeaking(true)
        }
        break
      case 'response.audio_transcript.done':
      case 'response.output_audio_transcript.done':
        if (typeof m.transcript === 'string' && m.transcript.trim()) setCaption({ speaker: 'voice', text: m.transcript.trim() })
        voiceTextRef.current = ''
        break
      case 'output_audio_buffer.started':
        sawBufferEventsRef.current = true
        setSpeaking(true)
        break
      case 'output_audio_buffer.stopped':
      case 'output_audio_buffer.cleared':
      case 'input_audio_buffer.speech_started':
        setSpeaking(false)
        break
      case 'response.done':
        if (!sawBufferEventsRef.current) setSpeaking(false)
        break
      case 'response.function_call_arguments.done':
        dispatchCall(String(m.name ?? ''), String(m.call_id ?? ''), typeof m.arguments === 'string' ? m.arguments : '{}')
        break
      case 'response.output_item.done': {
        const item = m.item as Record<string, unknown> | undefined
        if (item?.type === 'function_call') {
          dispatchCall(String(item.name ?? ''), String(item.call_id ?? ''), typeof item.arguments === 'string' ? item.arguments : '{}')
        }
        break
      }
      case 'error': {
        const e = m.error as { message?: string } | undefined
        if (e?.message) setError(e.message)
        break
      }
    }
  }

  const teardown = () => {
    const live = liveRef.current
    liveRef.current = null
    if (live) {
      live.handlers.current = NO_HANDLERS
      live.link.close()
    }
    setPhase('off')
    setSpeaking(false)
    setWaiting(false)
    setCaption(null)
  }

  const onOpen = () => {
    const live = liveRef.current
    if (!live || live.open) return
    live.open = true
    send({
      type: 'session.update',
      session: {
        // Semantic turn detection at low eagerness, as Douglas had (Adam, 16 Sep):
        // a short silence is not the end of the person's turn.
        turn_detection: { type: 'semantic_vad', eagerness: 'low', create_response: true, interrupt_response: true },
        input_audio_transcription: { model: 'gpt-4o-mini-transcribe' },
      },
    })
    send({
      type: 'response.create',
      response: { instructions: 'Say, in one short sentence, that you are listening and will pass what they say to the agent. Then stop and wait.' },
    })
    setPhase('on')
  }

  const onLost = (reason: string) => {
    setError(reason)
    teardown()
  }

  const handlers: LiveConnectHandlers = { onOpen, onEvent, onLost }

  const start = async () => {
    if (phase !== 'off') return
    const seq = ++startSeqRef.current
    setError(null)
    setPhase('connecting')
    const box = { current: handlers }
    const relay: LiveConnectHandlers = {
      onOpen: () => box.current.onOpen(),
      onEvent: (e) => box.current.onEvent(e),
      onLost: (r) => box.current.onLost(r),
    }
    const { customer, voice, connect = realtimeConnect } = optionsRef.current
    let link: LiveLink
    try {
      link = await connect({ customer, ...(voice ? { voice } : {}) }, relay)
    } catch (err) {
      if (seq !== startSeqRef.current) return
      setError(err instanceof Error ? err.message : 'Could not start the voice.')
      setPhase('off')
      return
    }
    if (seq !== startSeqRef.current) {
      link.close()
      return
    }
    sawBufferEventsRef.current = false
    liveRef.current = { link, handlers: box, awaiting: null, handledCalls: new Set(), open: false }
  }

  const stop = () => {
    startSeqRef.current++
    teardown()
  }

  // Trigger: the chat changing (a new message, a turn starting or ending, the
  // start screen's chat being created). Reports the awaited turn's reply once.
  const queuedCount = session.queuedMessages?.length ?? 0
  useEffect(() => {
    const live = liveRef.current
    const a = live?.awaiting
    if (!live || !a) return
    const s = sessionRef.current
    const id = s.session?.id ?? null
    if (a.sessionId !== id) {
      // The start screen's send created this chat: its first turn is the reply.
      if (a.sessionId === null && id) {
        a.sessionId = id
        a.sinceIndex = 0
      } else {
        setAwaiting(null)
        return
      }
    }
    if (s.queuedMessages?.length) return
    let userIdx = -1
    for (let i = s.messages.length - 1; i >= a.sinceIndex; i--) {
      if (s.messages[i].role === 'user') { userIdx = i; break }
    }
    const newError = s.error && s.error !== a.errorBefore ? s.error : null
    if (userIdx < 0) {
      if (newError && !s.isStreaming) {
        setAwaiting(null)
        say(agentReplyText(undefined, [], newError))
      }
      return
    }
    if (s.isStreaming) {
      a.sawStreaming = true
      return
    }
    let reply: AgentMessage | undefined
    for (let i = s.messages.length - 1; i > userIdx; i--) {
      if (s.messages[i].role === 'assistant') { reply = s.messages[i]; break }
    }
    if (!a.sawStreaming && !reply && !newError) return
    setAwaiting(null)
    const questions = [...(s.askedQuestions?.values() ?? [])].filter((q) => !q.answered)
    say(agentReplyText(reply, questions, s.error))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session.isStreaming, session.messages, session.session?.id, session.error, queuedCount])

  // Lifecycle: pick up a connection the start screen handed over; on unmount
  // close ours, or hand it over when its message is still creating the chat.
  useEffect(() => {
    if (handedOver) {
      const { live, timer } = handedOver
      clearTimeout(timer)
      handedOver = null
      live.handlers.current = handlers
      liveRef.current = live
      setPhase(live.open ? 'on' : 'connecting')
      setWaiting(live.awaiting !== null)
    }
    return () => {
      startSeqRef.current++
      const live = liveRef.current
      liveRef.current = null
      if (!live) return
      if (live.awaiting && live.awaiting.sessionId === null) {
        live.handlers.current = { ...NO_HANDLERS, onLost: () => { if (handedOver?.live === live) handedOver = null; live.link.close() } }
        const timer = setTimeout(() => {
          if (handedOver?.live !== live) return
          handedOver = null
          live.link.close()
        }, HANDOVER_MS)
        handedOver = { live, timer }
        return
      }
      live.handlers.current = NO_HANDLERS
      live.link.close()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // While a hook holds the connection, the link calls this render's handlers
  // (they read refs, so any render's would do; the latest keeps it simple).
  if (liveRef.current) liveRef.current.handlers.current = handlers

  const state: LiveState = phase === 'off' ? 'off'
    : phase === 'connecting' ? 'connecting'
    : speaking ? 'speaking'
    : waiting ? 'waiting'
    : 'listening'

  return { state, start, stop, caption, error }
}

/** Why the token route refused, in a sentence for the button. */
async function mintError(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as { message?: string; error?: string }
    if (body.message) return body.message
    if (body.error) return `The voice could not start: ${body.error}.`
  } catch {
    // not JSON: fall through to the status
  }
  return `The voice could not start (${res.status}).`
}

/**
 * The real connection: mint a realtime session (POST /assistant/realtime/token,
 * the voice front of T19), then WebRTC straight to Azure — the microphone out,
 * the voice back into an audio element, events on the `oai-events` channel.
 */
export const realtimeConnect: LiveConnect = async ({ customer, voice }, handlers) => {
  const auth = axios.defaults.headers.common['Authorization'] as string | undefined
  const res = await fetch(`${API_BASE_URL}/assistant/realtime/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...(auth ? { Authorization: auth } : {}) },
    body: JSON.stringify({ customer, ...(voice ? { voice } : {}) }),
  })
  if (res.status === 401) throw new Error('Your Platinum session has expired. Sign in again.')
  if (!res.ok) throw new Error(await mintError(res))
  const minted = (await res.json()) as { client_secret: string; webrtc_url: string; deployment: string }

  const pc = new RTCPeerConnection()
  const audio = new Audio()
  audio.autoplay = true
  let mic: MediaStream | null = null
  let closed = false
  const close = () => {
    if (closed) return
    closed = true
    mic?.getTracks().forEach((t) => t.stop())
    pc.close()
    audio.srcObject = null
  }

  let dc: RTCDataChannel
  // onOpen is called only once connect has returned, so the hook holds the link.
  let returned = false
  let opened = false
  try {
    pc.ontrack = (e) => {
      audio.srcObject = e.streams[0]
      audio.play().catch(() => { /* started from a click, so autoplay is allowed; nothing to do */ })
    }
    pc.onconnectionstatechange = () => {
      if (!closed && (pc.connectionState === 'failed' || pc.connectionState === 'disconnected')) {
        handlers.onLost('The voice connection was lost.')
      }
    }
    try {
      mic = await navigator.mediaDevices.getUserMedia({ audio: true })
    } catch {
      throw new Error('Talk needs the microphone. Allow it in the browser and try again.')
    }
    mic.getTracks().forEach((t) => pc.addTrack(t, mic as MediaStream))
    dc = pc.createDataChannel('oai-events')
    dc.onopen = () => {
      opened = true
      if (returned) handlers.onOpen()
    }
    dc.onmessage = (e) => {
      try {
        handlers.onEvent(JSON.parse(e.data as string) as Record<string, unknown>)
      } catch {
        /* not JSON: a keepalive */
      }
    }
    const offer = await pc.createOffer()
    await pc.setLocalDescription(offer)
    const sdpRes = await fetch(`${minted.webrtc_url}?model=${encodeURIComponent(minted.deployment)}`, {
      method: 'POST',
      body: offer.sdp,
      headers: { Authorization: `Bearer ${minted.client_secret}`, 'Content-Type': 'application/sdp' },
    })
    if (!sdpRes.ok) throw new Error(`The voice service refused the connection (${sdpRes.status}).`)
    await pc.setRemoteDescription({ type: 'answer', sdp: await sdpRes.text() })
  } catch (err) {
    close()
    throw err
  }
  returned = true
  if (opened) setTimeout(() => handlers.onOpen(), 0)
  return {
    send: (event) => { if (dc.readyState === 'open') dc.send(JSON.stringify(event)) },
    close,
  }
}
